import queryMyProfile from '../graphql/queries/queryMyProfile';
import queryMyAccounts from '../graphql/queries/queryMyAccounts';
import { HeadersWithoutApiKeyWorkaround } from './constants';
import { performImageUploadIfNeeded } from './imageAudioUploadHelper';
import updateProfileV2 from '../graphql/mutations/updateProfileV2';
import createSingleUseBillingURL from '../graphql/mutations/createSingleUseBillingURL';
import queryMyProfiles from '~/graphql/queries/queryMyProfiles';

export const state = () => ({
    myProfile: null,
    profilePosts: {},
    profilesByHandle: {},
    myAccounts: null,
});

export const actions = {
    async fetchMyProfile(context) {
        try {
            let request = {
                query: queryMyProfile.myProfile,
                context: { headers: { 'x-api-key': '' } }, // must be logged in
            };
            const result = await this.app.apolloProvider.defaultClient.query(request);

            const tokenProps = parseJwt(this.$auth.strategy.token.get());
            const user = {
                ...result.data.myProfile,
                account: {
                    isInternalUser: tokenProps['custom:is_internal_user'],
                    username: tokenProps['cognito:username'],
                },
                environment: result.data.myEnvironment,
                managedProfiles: result.data.myProfiles,
            };
            await this.app.$auth.setUser(user);
            if (user && user.language) {
                const languagePart = user.language.split('-');
                this.app.i18n.setLocale(languagePart[0]);
            }
            context.commit('setMyProfile', user);
        } catch (e) {
            if (e.message.includes('[404] profile not found')) {
                await this.app.router.push('/profile/deleted');
                throw new Error('Profile was deleted, redirecting user');
            }
            await context.dispatch('app/showServerError', {}, { root: true });
            console.error(e);
        }
    },

    async fetchMyProfiles(context) {
        try {
            let request = {
                query: queryMyProfiles.myProfiles,
                context: { headers: { 'x-api-key': '' } }, // must be logged in
            };
            const result = await this.app.apolloProvider.defaultClient.query(request);

            const tokenProps = parseJwt(this.$auth.strategy.token.get());

            return result.data.myProfiles
        } catch (e) {
            await context.dispatch('app/showServerError', {}, { root: true });
            console.error(e);
        }
    },

    async updateProfile(context, { profileImageData, whiteLabelImageData, name, handle, brandColor, brandLink, language, defaultPrivacyText, defaultTermsText, defaultTermsAndConditionsConsentText, defaultPrivacyPolicyConsentText, boostWords }) {
        const profileImage = await performImageUploadIfNeeded.bind(this)(profileImageData);
        const whiteLabelImage = await performImageUploadIfNeeded.bind(this)(whiteLabelImageData);

        let imageInput = null;
        let whiteLabelImageInput = null;

        if (profileImage) {
            imageInput = { name: profileImage.fileName };
        }

        if (whiteLabelImage) {
            whiteLabelImageInput = { name: whiteLabelImage.fileName };
        }

        const payload = {
            name: name,
            handle: handle,
            brandColor: brandColor,
            brandLink: brandLink,
            image: imageInput,
            whiteLabelImage: whiteLabelImageInput,
            language,
            defaultPrivacyText,
            defaultTermsText,
            defaultTermsAndConditionsConsentText,
            defaultPrivacyPolicyConsentText,
            boostWords,
        };

        let hasValues = false;
        Object.keys(payload).forEach((key) => {
            if (payload[key] !== null && payload[key] !== undefined) {
                hasValues = true;
                return;
            }
        });

        if (hasValues) {
            await this.app.apolloProvider.defaultClient.mutate({
                mutation: updateProfileV2.updateProfileV2,
                variables: payload,
                context: HeadersWithoutApiKeyWorkaround,
            });

            await context.dispatch('fetchMyProfile');
        }
    },

    async fetchMyAccounts(context) {
        try {
            const result = await this.app.apolloProvider.defaultClient.query({
                query: queryMyAccounts.myAccounts,
                fetchPolicy: 'no-cache',
                context: HeadersWithoutApiKeyWorkaround,
            });

            context.commit('setMyAccounts', result.data.myAccounts);
        } catch (e) {
            await context.dispatch('app/showServerError', {}, { root: true });
            console.error(e);
        }
    },

    async createSingleUseBillingURL() {
        const resp = await this.app.apolloProvider.defaultClient.mutate({
            mutation: createSingleUseBillingURL.createSingleUseBillingURL,
            context: HeadersWithoutApiKeyWorkaround,
        });
        return resp.data.createSingleUseBillingURL.url;
    },
};

export const mutations = {
    resetMyProfile(state) {
        state.myProfile = null;
    },
    setMyProfile(state, myProfile) {
        if (myProfile != null && (myProfile.environment == null || myProfile.account == null || myProfile.managedProfiles == null)) {
            throw new Error('not all required profile fields present, compare result with `myProfile` response');
        }
        state.myProfile = myProfile;
    },
    setMyAccounts(state, results) {
        state.myAccounts = results;
    },
};

export const getters = {
    getMyProfile: (state) => {
        return state.myProfile;
    },
    getMyAccounts: (state) => {
        return state.myAccounts;
    },
};

const parseJwt = (token) => {
    try {
        return JSON.parse(atob(token.split('.')[1]));
    } catch (e) {
        return null;
    }
};
